import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './StatReportsSummaryPage.module.css';
import commonStyles from '../../common/CommonStyles.module.css';
import { useNavigate } from 'react-router-dom';

const StatReportsSummaryPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/finance/stat-reports/summary');
      setSummary(response.data);
      setError(null);
    } catch (e) {
      console.error('Ошибка при загрузке сводных данных:', e);
      setError('Не удалось загрузить сводные таблицы');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    return value.toLocaleString('ru-RU');
  };

  const formatDecimal = (value) => {
    if (!value && value !== 0) {
      return '';
    }
    return Number(value).toFixed(2);
  };

  const metrics = [
    { key: 'primaryDivisionsCount', label: 'Первичных отделений' },
    { key: 'localDivisionsCount', label: 'Местных отделений' },
    { key: 'membersTotal', label: 'Членов ЛКСМ РФ (всего)' },
    { key: 'membersUnder18', label: 'До 18 лет' },
    { key: 'membersOver35', label: 'Старше 35 лет' },
    { key: 'averageAge', label: 'Средний возраст', isAverage: true },
    { key: 'membersEmployees', label: 'Служащие' },
    { key: 'membersWorkers', label: 'Рабочие' },
    { key: 'membersStudents', label: 'Студенты' },
    { key: 'membersSchoolchildren', label: 'Школьники' },
    { key: 'membersUnemployed', label: 'Безработные' },
    { key: 'membersPayingDues', label: 'Уплачивающих взносы' },
    { key: 'membersDepartedTotal', label: 'Выбывших (всего)' },
    { key: 'membersExcluded', label: 'Исключённых' },
    { key: 'membersJoined', label: 'Вступивших' },
    { key: 'committeeMembersCount', label: 'Членов Комитета' },
    { key: 'bureauMembersCount', label: 'Членов Бюро' },
    { key: 'secretariesCount', label: 'Секретарей Комитета' },
    { key: 'staffWorkersCount', label: 'Штатных работников' },
    { key: 'deputiesRegionalLegislative', label: 'Депутаты (региональный уровень)' },
    { key: 'deputiesLocalRepresentative', label: 'Депутаты (МСУ)' },
    { key: 'assistantsPermanent', label: 'Помощники (штатные)' },
    { key: 'assistantsVoluntary', label: 'Помощники (общественные)' },
    { key: 'youthCouncilRegional', label: 'Молодёжные советы (регион)' },
    { key: 'youthCouncilLocal', label: 'Молодёжные советы (МСУ)' },
    { key: 'pioneerMembersCount', label: 'Членов пионерской организации' },
    { key: 'discourseMembersCount', label: 'Членов НСП «Дискурс»' },
    { key: 'studyCirclesTotal', label: 'Кружков/школ/семинаров (всего)' },
    { key: 'studyCirclesAttendees', label: 'Посещающих занятия' },
    { key: 'socialNetworksCount', label: 'Подписчиков в соцсетях (всего)' }
  ];

  if (loading) {
    return (
      <div className={commonStyles.pageContainer}>
        <div className={commonStyles.contentContainer}>
          <div className={commonStyles.content}>
            <div className={styles.loading}>Загрузка сводных таблиц...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={commonStyles.pageContainer}>
      <div className={commonStyles.contentContainer}>
        <div className={commonStyles.content}>
          <div className={styles.headerRow}>
            <button
              className={styles.backButton}
              onClick={() => navigate('/admin/stat-reports')}
            >
              ← К списку отчётов
            </button>
            <h2 className={styles.title}>Сводные таблицы статистических отчётов</h2>
          </div>

          {error && (
            <div className={styles.error}>
              {error}
              <button onClick={fetchSummary} className={styles.retryButton}>
                Попробовать снова
              </button>
            </div>
          )}

          {!summary || !summary.districts || summary.districts.length === 0 ? (
            <div className={styles.noData}>Нет данных для отображения сводных таблиц</div>
          ) : (
            <>
              {summary.districts.map((district) => (
                <div key={district.districtId || district.name} className={styles.districtBlock}>
                  <h3 className={styles.districtTitle}>
                    {district.name || 'Федеральный округ'}{district.code ? ` (${district.code})` : ''}
                  </h3>

                  <div className={styles.tableContainer}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Регион</th>
                          {metrics.map((metric) => (
                            <th key={metric.key}>{metric.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {!district.regions || district.regions.length === 0 ? (
                          <tr>
                            <td colSpan={1 + metrics.length} className={styles.noData}>
                              Нет данных по регионам
                            </td>
                          </tr>
                        ) : (
                          district.regions.map((region) => (
                            <tr key={region.regionId || region.name}>
                              <td>{region.name || 'Регион'}</td>
                              {metrics.map((metric) => {
                                const value = region[metric.key];
                                if (metric.isAverage) {
                                  return (
                                    <td key={metric.key} className={styles.numberCell}>
                                      {value != null ? formatDecimal(value) : ''}
                                    </td>
                                  );
                                }
                                return (
                                  <td key={metric.key} className={styles.numberCell}>
                                    {formatNumber(value)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                        )}

                        {district.total && (
                          <tr className={styles.totalRow}>
                            <td>Сумма / среднее по округу</td>
                            {metrics.map((metric) => {
                              const value = district.total[metric.key];
                              if (metric.isAverage) {
                                return (
                                  <td key={metric.key} className={styles.numberCell}>
                                    {value != null ? formatDecimal(value) : ''}
                                  </td>
                                );
                              }
                              return (
                                <td key={metric.key} className={styles.numberCell}>
                                  {formatNumber(value)}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {summary.organizationTotal && (
                <div className={styles.districtBlock}>
                  <h3 className={styles.districtTitle}>Итого по всей организации</h3>

                  <div className={styles.tableContainer}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Организация</th>
                          {metrics.map((metric) => (
                            <th key={metric.key}>{metric.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className={styles.totalRow}>
                          <td>Сумма / средний возраст</td>
                          {metrics.map((metric) => {
                            const value = summary.organizationTotal[metric.key];
                            if (metric.isAverage) {
                              return (
                                <td key={metric.key} className={styles.numberCell}>
                                  {value != null ? formatDecimal(value) : ''}
                                </td>
                              );
                            }
                            return (
                              <td key={metric.key} className={styles.numberCell}>
                                {formatNumber(value)}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatReportsSummaryPage;

